/**
 * Tag Color System — preset palette with user override support.
 *
 * Each tag is assigned a color by hashing its name into the preset palette.
 * Users can override individual tag colors via their settings (tagColors field).
 */

// Preset color palette (12 distinct, accessible colors) — used for hash-based fallback
const PALETTE = [
  '#2196F3', // blue (ocean)
  '#34C759', // green
  '#FF9500', // orange
  '#AF52DE', // purple
  '#FF2D55', // pink
  '#5AC8FA', // teal
  '#FFD60A', // yellow
  '#FF6482', // coral
  '#30B0C7', // cyan
  '#A2845E', // brown
  '#8E8E93', // gray
  '#64D2FF', // sky
];

// Colors reserved for event status indicators — should not be manually assigned to tags
export const RESERVED_TAG_COLORS = new Set([
  '#34C759', // active/oneTime status green
  '#8E8E93', // done status gray
]);

// Extended palette for manual color picking (excludes reserved status colors)
export const SELECTABLE_PALETTE = [
  '#2196F3', // blue (ocean)
  '#FF9500', // orange
  '#AF52DE', // purple
  '#FF2D55', // pink
  '#5AC8FA', // teal
  '#FFD60A', // yellow
  '#FF6482', // coral
  '#30B0C7', // cyan
  '#A2845E', // brown
  '#64D2FF', // sky
  '#E84855', // red
  '#2D6A4F', // forest
  '#7209B7', // violet
  '#F72585', // magenta
  '#4CC9F0', // light blue
  '#FB8500', // amber
  '#606C38', // olive
  '#9B5DE5', // lavender
  '#FFD54F', // gold
];

/** Simple hash to assign a stable index per tag name */
function hashTag(tag: string): number {
  let h = 0;
  const s = tag.toLowerCase().trim();
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Normalize a tag color overrides map so all keys are lowercase-trimmed.
 * Deduplicates by keeping the last-written value for each normalized key.
 */
export function normalizeTagColorKeys(overrides: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(overrides)) {
    result[k.toLowerCase().trim()] = v;
  }
  return result;
}

/**
 * Get the color for a tag.
 * @param tag - Tag name
 * @param userOverrides - Optional user-defined color overrides { tagName: hex }
 */
export function getTagColor(tag: string, userOverrides?: Record<string, string> | null): string {
  const key = tag.toLowerCase().trim();
  if (userOverrides?.[key]) return userOverrides[key];
  return PALETTE[hashTag(key) % PALETTE.length];
}

/**
 * Get the primary tag color for a reminder's tag string.
 * Uses the first tag, or returns null if no tags.
 */
export function getPrimaryTagColor(tags: string | null | undefined, userOverrides?: Record<string, string> | null): string | null {
  if (!tags) return null;
  const first = tags.split(',').map(t => t.trim()).filter(Boolean)[0];
  if (!first) return null;
  return getTagColor(first, userOverrides);
}

export { PALETTE as TAG_PALETTE };
